import React from "react";
import { createPortal } from "react-dom";

const GAP = 8;
const EDGE_PADDING = 8;
const MAX_WIDTH = 280;

/* Tooltip — hover/focus dark bubble. Wraps a trigger element.
   Bubble is portaled to document.body and positioned in fixed coords so it
   escapes ancestor `overflow` clipping and z-index/stacking-context traps
   (e.g. sticky page headers, scrollable tables). Position flips to the
   opposite side and clamps to the viewport when there isn't room. */
export function Tooltip({ label, children, side = "top", style, ...rest }) {
  const [show, setShow] = React.useState(false);
  const [coords, setCoords] = React.useState(null);
  const triggerRef = React.useRef(null);
  const bubbleRef = React.useRef(null);

  const recompute = React.useCallback(() => {
    const trigger = triggerRef.current;
    const bubble = bubbleRef.current;
    if (!trigger || !bubble) return;
    const t = trigger.getBoundingClientRect();
    const b = bubble.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;

    const fits = {
      top: t.top - GAP - b.height >= 0,
      bottom: t.bottom + GAP + b.height <= vh,
      left: t.left - GAP - b.width >= 0,
      right: t.right + GAP + b.width <= vw,
    };
    let placement = side;
    if (!fits[placement]) {
      const opposite = { top: "bottom", bottom: "top", left: "right", right: "left" }[side];
      if (fits[opposite]) placement = opposite;
    }

    let top;
    let left;
    if (placement === "top" || placement === "bottom") {
      top = placement === "top" ? t.top - GAP - b.height : t.bottom + GAP;
      left = t.left + t.width / 2 - b.width / 2;
    } else {
      left = placement === "left" ? t.left - GAP - b.width : t.right + GAP;
      top = t.top + t.height / 2 - b.height / 2;
    }

    left = Math.min(Math.max(left, EDGE_PADDING), vw - b.width - EDGE_PADDING);
    top = Math.min(Math.max(top, EDGE_PADDING), vh - b.height - EDGE_PADDING);

    setCoords({ top, left });
  }, [side]);

  React.useLayoutEffect(() => {
    if (!show) {
      setCoords(null);
      return;
    }
    recompute();
    window.addEventListener("scroll", recompute, true);
    window.addEventListener("resize", recompute);
    return () => {
      window.removeEventListener("scroll", recompute, true);
      window.removeEventListener("resize", recompute);
    };
  }, [show, recompute]);

  return (
    <span
      ref={triggerRef}
      style={{ position: "relative", display: "inline-flex", ...style }}
      onMouseEnter={() => setShow(true)}
      onMouseLeave={() => setShow(false)}
      onFocusCapture={() => setShow(true)}
      onBlurCapture={() => setShow(false)}
      {...rest}
    >
      {children}
      {show
        ? createPortal(
            <span
              ref={bubbleRef}
              role="tooltip"
              style={{
                position: "fixed",
                zIndex: 1400,
                top: coords ? coords.top : -9999,
                left: coords ? coords.left : -9999,
                visibility: coords ? "visible" : "hidden",
                background: "var(--slate)",
                color: "var(--white)",
                fontFamily: "var(--font-sans)",
                fontWeight: 500,
                fontSize: 12,
                lineHeight: 1.35,
                padding: "6px 10px",
                borderRadius: "var(--radius-xs)",
                boxShadow: "var(--shadow-3)",
                whiteSpace: "normal",
                width: "max-content",
                maxWidth: MAX_WIDTH,
                pointerEvents: "none",
              }}
            >
              {label}
            </span>,
            document.body
          )
        : null}
    </span>
  );
}

export default Tooltip;
